// src/managers/PanelManager.jsx
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/* ========================
 * Unified Panel Manager - Single portal for all content types
 * Uses CodeMirror as universal renderer with content-aware extensions
 * ======================== */

export const CONTENT_TYPES = {
    CODE: 'code',
    MERMAID: 'mermaid',
    DOCUMENT: 'document'
};

export const RightSidebarViews = {
    WORKSPACE: 'workspace',
    CODE: 'code',
    DOCUMENT: 'document'
};

export const PanelTypes = {
    NOTES: 'notes',
    SNIPPETS: 'snippets',
    PORTAL: 'portal',
    CODER: 'coder',
    EVOK_LOCAL: 'evok_local' // Added new panel type
};

const DEFAULT_PANEL_STATE = {
    [PanelTypes.NOTES]: false,
    [PanelTypes.SNIPPETS]: false,
    [PanelTypes.PORTAL]: false,
    [PanelTypes.CODER]: false,
    [PanelTypes.EVOK_LOCAL]: false // Added default state
};

const SHARED_RIGHT_SIDEBAR_DEFAULT_WIDTH = 65;

const DEFAULT_PANEL_WIDTHS = {
    [PanelTypes.NOTES]: 30,
    [PanelTypes.SNIPPETS]: 35,
    [PanelTypes.PORTAL]: SHARED_RIGHT_SIDEBAR_DEFAULT_WIDTH,
    [PanelTypes.CODER]: SHARED_RIGHT_SIDEBAR_DEFAULT_WIDTH,
    [PanelTypes.EVOK_LOCAL]: 35 // Added default width
};

const PanelManagerContext = createContext();

export const PanelManagerProvider = ({ children }) => {
    const [panelState, setPanelState] = useState({ ...DEFAULT_PANEL_STATE });
    const [panelWidths, setPanelWidths] = useState({ ...DEFAULT_PANEL_WIDTHS });
    const [rightSidebarView, setRightSidebarViewState] = useState(RightSidebarViews.WORKSPACE);
    // portalContent can be:
    // null: Portal is not showing external content or is closed.
    // { type, content, metadata: { portalId, ...otherMeta }, isStreaming: false }: Static content.
    // { type, accumulatedContent, metadata: { portalId, ...otherMeta }, isStreaming: true }: Streaming content.
    const [portalContent, setPortalContent] = useState(null);

    const togglePanelWithMemory = useCallback((panelType) => {
        if (panelType === PanelTypes.SNIPPETS) {
            setPanelState(prevState => ({
                ...prevState,
                [PanelTypes.SNIPPETS]: !prevState[PanelTypes.SNIPPETS],
            }));
            return;
        }

        if (panelType === PanelTypes.CODER) {
            setRightSidebarViewState(RightSidebarViews.WORKSPACE);
            return;
        }

        if (panelType === PanelTypes.PORTAL) {
            setRightSidebarViewState(currentView => (
                currentView === RightSidebarViews.CODE || currentView === RightSidebarViews.DOCUMENT
                    ? RightSidebarViews.WORKSPACE
                    : RightSidebarViews.CODE
            ));
            return;
        }
    }, []);

    const isPanelOpen = useCallback((panelType) => {
        if (panelType === PanelTypes.SNIPPETS) {
            return !!panelState[PanelTypes.SNIPPETS];
        }

        if (panelType === PanelTypes.CODER) {
            return rightSidebarView === RightSidebarViews.WORKSPACE;
        }

        if (panelType === PanelTypes.PORTAL) {
            return rightSidebarView === RightSidebarViews.CODE || rightSidebarView === RightSidebarViews.DOCUMENT;
        }

        return false;
    }, [panelState, rightSidebarView]);

    const setRightSidebarView = useCallback((view) => {
        setRightSidebarViewState(view);
    }, []);

    const setPanelWidth = useCallback((panelType, width) => {
        setPanelWidths(prev => ({
            ...prev,
            [panelType]: width
        }));
    }, []);

    // For showing static, complete content in the portal
    const showInPortal = useCallback((contentPayload) => {
        const meta = contentPayload.metadata || {};
        let contentType = contentPayload.type;

        if (!contentType) {
            if (meta.language === 'mermaid') {
                contentType = CONTENT_TYPES.MERMAID;
            } else if (meta.language) {
                contentType = CONTENT_TYPES.CODE;
            } else {
                contentType = CONTENT_TYPES.DOCUMENT;
            }
        }
        
        const finalContent = contentType === CONTENT_TYPES.DOCUMENT 
            ? (contentPayload.content || '').replace(/^<document>\n?/, '').replace(/\n?<\/document>$/, '')
            : contentPayload.content || '';

        const portalId = meta.portalId || `portal-static-${Date.now()}`;

        setPortalContent({
            ...contentPayload, 
            type: contentType,
            metadata: {
                ...meta,
                contentType,
                portalId: portalId 
            },
            isStreaming: false,
            accumulatedContent: finalContent,
            content: finalContent,
        });

        setRightSidebarViewState(contentType === CONTENT_TYPES.DOCUMENT ? RightSidebarViews.DOCUMENT : RightSidebarViews.CODE);
    }, []);

    // --- Streaming specific functions ---
    const openPortalForStream = useCallback(({ portalId, type = CONTENT_TYPES.CODE, language, openingFence = '', metadata = {} }) => {
        if (!portalId) {
            console.error("openPortalForStream called without a portalId.");
            return;
        }
        
        const streamType = type || CONTENT_TYPES.CODE;

        setPortalContent({
            type: streamType, 
            metadata: {
                ...metadata, 
                language,
                contentType: streamType,
                portalId: portalId 
            },
            accumulatedContent: streamType === CONTENT_TYPES.CODE ? openingFence : '',
            isStreaming: true,
            content: null,
        });
        setRightSidebarViewState(streamType === CONTENT_TYPES.DOCUMENT ? RightSidebarViews.DOCUMENT : RightSidebarViews.CODE);
    }, []);

    const appendToPortalStream = useCallback(({ portalId, chunk }) => {
        setPortalContent(current => {
            if (current && current.isStreaming && current.metadata?.portalId === portalId) {
                return {
                    ...current,
                    accumulatedContent: (current.accumulatedContent || '') + chunk,
                };
            }
            return current; 
        });
    }, []);

    const finalizePortalStream = useCallback(({ portalId, finalContent: providedFinalContent, closingFenceChunk = '' }) => {
        setPortalContent(current => {
            if (current && current.isStreaming && current.metadata?.portalId === portalId) {
                let finalContent = providedFinalContent !== undefined
                    ? providedFinalContent
                    : (current.accumulatedContent || '') + (current.type === CONTENT_TYPES.CODE ? closingFenceChunk : '');
                
                // Defensively clean document tags to match showInPortal's behavior.
                if (current.type === CONTENT_TYPES.DOCUMENT) {
                    finalContent = (finalContent || '').replace(/^<document>\n?/, '').replace(/\n?<\/document>$/, '');
                }
                
                return {
                    ...current,
                    content: finalContent, 
                    accumulatedContent: finalContent,
                    isStreaming: false,
                };
            }
            return current;
        });
    }, []);
    
    const cancelPortalStream = useCallback(({ portalId, errorMessage = "\n[Stream cancelled]" }) => {
        setPortalContent(current => {
            if (current && current.isStreaming && current.metadata?.portalId === portalId) {
                const finalContent = (current.accumulatedContent || '') + errorMessage;
                return {
                    ...current,
                    content: finalContent, 
                    accumulatedContent: finalContent,
                    isStreaming: false,
                };
            }
            return current;
        });
    }, []);

    const commitStreamToFinalId = useCallback(({ currentPortalId, finalPortalId, finalMetadata }) => {
        setPortalContent(current => {
            if (current && current.metadata?.portalId === currentPortalId) {
                const stillStreamingWhenCommitted = current.isStreaming && !current.content;
                if (stillStreamingWhenCommitted) {
                    console.warn("Committing a stream that wasn't properly finalized/cancelled. Content might be based on accumulated value.", currentPortalId);
                }
                return {
                    ...current,
                    metadata: {
                        ...(current.metadata || {}), 
                        ...(finalMetadata || {}),   
                        portalId: finalPortalId 
                    },
                    isStreaming: false, 
                    content: current.content || current.accumulatedContent || '', 
                };
            }
            return current;
        });
    }, []);

    const getPortalContent = useCallback(() => portalContent, [portalContent]);
    
    const clearPortalContent = useCallback(() => {
        setPortalContent(null);
    }, []);

    const contextValue = useMemo(() => ({
        panelState,
        rightSidebarView,
        setRightSidebarView,
        togglePanelWithMemory,
        isPanelOpen,
        panelWidths,
        setPanelWidth,
        showInPortal,
        getPortalContent,
        clearPortalContent,
        openPortalForStream,
        appendToPortalStream,
        finalizePortalStream,
        cancelPortalStream,
        commitStreamToFinalId,
    }), [
        panelState,
        rightSidebarView,
        setRightSidebarView,
        togglePanelWithMemory,
        isPanelOpen,
        panelWidths,
        setPanelWidth,
        showInPortal,
        getPortalContent,
        clearPortalContent,
        openPortalForStream,
        appendToPortalStream,
        finalizePortalStream,
        cancelPortalStream,
        commitStreamToFinalId,
    ]);

    return (
        <PanelManagerContext.Provider value={contextValue}>
            {children}
        </PanelManagerContext.Provider>
    );
};

export const usePanelManager = () => {
    const context = useContext(PanelManagerContext);
    if (!context) {
        throw new Error('usePanelManager must be used within a PanelManagerProvider');
    }
    return context;
};

export const usePanelState = usePanelManager;